import { useEffect, useRef, useState } from 'react';
import { onSnapshot, type Query, type FirestoreError } from 'firebase/firestore';

import { api, buildUrl, isApiQuery, type ApiQuery } from '@/services/api/client';

interface State<T> {
  data: T[];
  loading: boolean;
  error: FirestoreError | null;
}

const POLL_MS = 5000;

// Caché en memoria por URL (modo API). Hace que al volver a una pantalla se vean
// los datos al instante (stale-while-revalidate) en vez del "cargando" de cero.
const apiCache = new Map<string, any[]>();

export function useFirestoreCollection<T>(q: Query<T> | ApiQuery | null) {
  const key = isApiQuery(q) ? buildUrl(q) : null;
  const [state, setState] = useState<State<T>>(() => {
    if (key && apiCache.has(key)) {
      return { data: apiCache.get(key) as T[], loading: false, error: null };
    }
    return { data: [], loading: true, error: null };
  });
  const savedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!q) {
      setState({ data: [], loading: false, error: null });
      return;
    }

    // --- Modo API: fetch + polling, con caché stale-while-revalidate ---
    if (isApiQuery(q)) {
      let cancelled = false;
      let timer: any;
      const url = buildUrl(q);
      savedKey.current = url;
      // Si hay algo en caché, lo mostramos ya y refrescamos sin spinner.
      const cached = apiCache.get(url);
      if (cached) setState({ data: cached as T[], loading: false, error: null });
      const fetchOnce = async (showLoading: boolean) => {
        if (showLoading && !cached) setState((s) => ({ ...s, loading: true }));
        try {
          const data = await api.get(url);
          if (!cancelled) {
            apiCache.set(url, data ?? []);
            setState({ data: data ?? [], loading: false, error: null });
          }
        } catch (e: any) {
          if (!cancelled) setState((s) => ({ ...s, loading: false, error: e }));
        }
      };
      fetchOnce(true);
      timer = setInterval(() => fetchOnce(false), POLL_MS);
      return () => {
        cancelled = true;
        clearInterval(timer);
      };
    }

    // --- Modo Firebase: realtime ---
    const unsub = onSnapshot(
      q as Query<T>,
      (snap) => {
        const data = snap.docs.map((d) => d.data());
        setState({ data, loading: false, error: null });
      },
      (error) => {
        setState({ data: [], loading: false, error });
      },
    );
    return unsub;
    // OJO: en modo API dependemos de `key` (la URL, string estable). NO de `q`,
    // porque varias pantallas crean la query en línea (ej. ventasPendientes('danna'))
    // y devuelven un objeto nuevo en cada render; si dependiéramos de `q` el efecto
    // se reejecutaría sin parar → loop de fetches y "cargando" eterno. En modo
    // Firebase `key` es null, así que caemos a `q` (que ahí sí es estable/memoizado).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key ?? q]);

  return state;
}
